import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { parseSseJson, subscribeSse, type LiveStatus } from './sse';
import { queryKeys } from './queryKeys';
import type { Job } from '../types';

interface SnapshotPayload {
  jobs: Job[];
}
interface StatePayload {
  jobId: string;
  job: Job;
}

/**
 * A hard failure of the live channel, latched until the connection recovers. `status` is the
 * HTTP status the wrapper's probe recovered, or `null` when there was no HTTP response at
 * all (offline / DNS / refused). A1EE-364.
 */
export interface LiveFailure {
  status: number | null;
}

export interface UserJobsSubscription {
  status: LiveStatus | null;
  /** Non-null while the channel is hard-failed. Cleared automatically once it reopens. */
  failure: LiveFailure | null;
}

/**
 * SSE-driven replacement for `useJobs` polling. Opens `/api/v1/jobs/events?userId=X`
 * and writes incoming `snapshot` / `state` events into the TanStack cache under
 * `queryKeys.jobs(userId)` via `setQueryData`. `useJobs` reads that cache — it no
 * longer polls. The returned `status` drives the dashboard's `RefreshPill`; `failure`
 * drives the app-wide `LiveStatusBanner`.
 */
export function useUserJobsSubscription(userId: string | undefined): UserJobsSubscription {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LiveStatus | null>(null);
  const [failure, setFailure] = useState<LiveFailure | null>(null);

  useEffect(() => {
    if (!userId) return;
    const key = queryKeys.jobs(userId);
    const sub = subscribeSse(`/api/v1/jobs/events?userId=${encodeURIComponent(userId)}`, {
      onStatus: (next) => {
        setStatus(next);
        // A reopen means whatever the probe diagnosed is over. Latching the failure until
        // this point (rather than clearing on the first retry attempt) is what stops the
        // banner from flickering while the wrapper's reopen loop grinds against a backend
        // that is still down.
        if (next === 'live') setFailure(null);
      },
      onHardFailure: (httpStatus) => setFailure({ status: httpStatus }),
      onEvent: (event, data) => {
        // Per-branch parse via `parseSseJson` (defense-in-depth alongside the
        // empty/undefined-data guard in `subscribeSse`): a malformed payload
        // logs a warning and is dropped instead of crashing the subscription.
        // See A1EE-295.
        if (event === 'snapshot') {
          const payload = parseSseJson<SnapshotPayload>(event, data);
          if (!payload) return;
          queryClient.setQueryData<Job[]>(key, payload.jobs);
        } else if (event === 'state') {
          const payload = parseSseJson<StatePayload>(event, data);
          if (!payload) return;
          queryClient.setQueryData<Job[]>(key, (old) => {
            if (!old) return [payload.job];
            const idx = old.findIndex((j) => j.id === payload.jobId);
            if (idx === -1) return [payload.job, ...old];
            const next = old.slice();
            next[idx] = payload.job;
            return next;
          });
        }
      },
    });
    return () => sub.close();
  }, [userId, queryClient]);

  return { status, failure };
}
